import React from 'react';
import { Typography, Space, Tag, Tooltip, theme } from 'antd';
import { formatDateTime, formatDateTimeCompact, parseApiDate } from '../utils/datetime';

const { Text } = Typography;

// Report shapes, palettes and the two status indicators, in one module.
//
// They live here because the list page and the detail page both render them.
// Two copies of a colour map do not stay equal: one gains a state the other
// doesn't, and the same report then reads as two different severities
// depending on which screen you opened it from.

export interface StatusBreakdown {
  total: number;
  healthy: number;
  degraded: number;
  zombie: number;
  error: number;
  down: number;
  suspect: number;
  unknown: number;
  // Stale = continuously DOWN/ERROR for ≥ staleThresholdHours. Reported
  // separately so chronic dead targets don't drown out fresh outages.
  stale: number;
}

export interface CountryBreakdown {
  country: string;
  networks: StatusBreakdown;
  proxies: StatusBreakdown;
}

export interface SourceBreakdown {
  source: string;
  networks: StatusBreakdown;
  proxies: StatusBreakdown;
}

export interface ReportSummary {
  networks: StatusBreakdown;
  proxies: StatusBreakdown;
}

export interface TopIssue {
  errorType: string;
  country: string;
  count: number;
}

export interface ReportTargetResult {
  url: string;
  success: boolean;
  latency: number;
  error?: string;
  // The target was never requested — disabled fleet-wide, or cut off by the
  // cascade's time budget. Neither a success nor a failure of this proxy.
  skipped?: boolean;
}

export interface ReportStepResult {
  name: string;
  success: boolean;
  latency: number;
  error?: string;
  targetResults?: ReportTargetResult[];
}

export interface StatusChange {
  state: string;
  // Stamped by the gateway in UTC when the transition was confirmed.
  at: string;
}

export interface ReportCheckItem {
  ip: string; // "host:port" on proxy items — the port is part of the identity
  // protocol/label are stamped on proxy items at generation time. Absent on
  // IP items and on reports generated before they were recorded.
  protocol?: string;
  label?: string;
  type: string; // "ip" or "proxy"
  state: string;
  failedStep?: string;
  error?: string;
  steps: ReportStepResult[];
  latency: number;
  totalDuration: number;
  workerName?: string;
  workerIP?: string;
  workerCountry?: string;
  // How the target reached this state, oldest first. Absent on reports
  // generated before the timeline was recorded.
  statusTimeline?: StatusChange[];
}

export interface ReportServerDetail {
  serverKey: string;
  serverName: string;
  mainIP: string;
  country: string;
  sourceKey?: string;
  sourceId?: string;
  sourceUrl?: string;
  items: ReportCheckItem[];
  // deleted is set by the backend at response time when the server is no
  // longer in the live `servers` collection. We route the drill-in link to
  // the snapshot page instead of the live health page in that case.
  deleted?: boolean;
}

export interface Report {
  id: string;
  date: string;
  summary: ReportSummary;
  countries: CountryBreakdown[];
  sources: SourceBreakdown[];
  topIssues: TopIssue[];
  servers: ReportServerDetail[];
  generatedAt: string;
}

// The status palette is the app's, not this screen's. These hexes are the same
// ones health/statusVisuals.tsx draws its sparklines with (SPARKLINE_COLORS) —
// a DEGRADED proxy has to be the same colour on the report as on the health
// page, or the operator learns two palettes and trusts neither. Previously
// degraded and suspect were a shade off here, which read as a third state.
export const STATE_COLORS: Record<string, string> = {
  healthy: '#52c41a',
  degraded: '#fa8c16',
  zombie: '#722ed1',
  error: '#ff4d4f',
  down: '#cf1322',
  suspect: '#faad14',
  unknown: '#8c8c8c',
  // Distinct dark-grey for stale so it visually separates from the red
  // outage band and from the lighter "unknown" gray in the same stacked bar.
  stale: '#595959',
};

export const STATE_LABELS: Record<string, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  zombie: 'Zombie',
  error: 'Error',
  down: 'Down',
  suspect: 'Suspect',
  unknown: 'Unknown',
  stale: 'Stale',
};

// Tag colours are the app's status vocabulary, copied from
// health/statusVisuals.tsx STATUS_COLORS. This map used to disagree with it —
// DEGRADED was orange here and gold there, ERROR volcano here and red there —
// so the same failure changed colour when the operator moved between the
// health page and the report. It now says exactly what the rest of the app says.
export const STATE_TAG_COLORS: Record<string, string> = {
  HEALTHY: 'green',
  DEGRADED: 'gold',
  SUSPECT: 'yellow',
  ZOMBIE: 'purple',
  ERROR: 'red',
  DOWN: 'red',
  UNKNOWN: 'default',
  STALE: 'default',
};

// The breakdown counters key off lowercase state names. Derived rather than
// written out twice: two hand-maintained copies of one palette drift, and the
// summary tags then disagree with the table rows in the same card.
export const TAG_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_TAG_COLORS).map(([state, colour]) => [state.toLowerCase(), colour]),
);

export const STEP_LABELS: Record<string, string> = {
  icmp_ping: 'ICMP Ping',
  globalping: 'Global Visibility',
  proxy_connect: 'Proxy TCP Connect',
  proxy_request: 'Proxy HTTP Request',
  proxy_latency: 'Tunnel Latency',
  proxy_egress_geo: 'Egress Geo',
  tcp_connect: 'TCP Connect',
  protocol_handshake: 'Protocol Handshake',
  traffic_test: 'Traffic Test',
  performance_test: 'Performance Test',
};

// STATE_PRIORITY orders states by severity. It drives both "worst first"
// sorting and the per-server rollup tags, which is why it is one map: sorting a
// list by one severity order and labelling its rows by another produces a table
// that argues with itself.
//
// STALE sits below the active failure states deliberately — it is settled
// long-term failure, and a server with a fresh outage should outrank a server
// whose modem has been dead for a week.
export const STATE_PRIORITY: Record<string, number> = {
  HEALTHY: 0,
  UNKNOWN: 1,
  STALE: 2,
  SUSPECT: 3,
  DEGRADED: 4,
  ZOMBIE: 5,
  ERROR: 6,
  DOWN: 7,
};

// PROBLEM_STATES is what "only problems" means, and it matches the backend's
// own filter in reports.Generate — a server reaches the report at all only if
// one of its items is in one of these states.
export const PROBLEM_STATES = new Set(['DEGRADED', 'ZOMBIE', 'ERROR', 'DOWN', 'STALE']);

export function worstState(items: ReportCheckItem[]): string {
  return items.reduce(
    (w, i) => ((STATE_PRIORITY[i.state] ?? 0) > (STATE_PRIORITY[w] ?? 0) ? i.state : w),
    'HEALTHY',
  );
}

export function serverWorstRank(s: ReportServerDetail): number {
  return (s.items || []).reduce((m, i) => Math.max(m, STATE_PRIORITY[i.state] ?? 0), 0);
}

export function hasProblem(s: ReportServerDetail): boolean {
  return (s.items || []).some((i) => PROBLEM_STATES.has(i.state));
}

// ---------------------------------------------------------------------------
// Freshness. A failure list mixes an outage that started an hour ago with a
// modem that has been dead for three weeks, and until now they looked
// identical — STALE was a grey tag you had to know the meaning of. The
// escalation chain already carries the answer, so the age is read off it and
// printed next to the state. No extra request, no extra click.
// ---------------------------------------------------------------------------

/** CHRONIC_HOURS is where "still happening" turns into "settled long ago". It
 *  mirrors the gateway's own default StaleThresholdHours (24h). */
export const CHRONIC_HOURS = 24;

/** problemSince is when the target first entered a failing state, taken from
 *  the recorded escalation chain. Null on reports generated before timelines
 *  were recorded, and on targets that never failed. */
export function problemSince(item: ReportCheckItem): string | null {
  for (const change of item.statusTimeline || []) {
    if (PROBLEM_STATES.has(change.state)) return change.at;
  }
  return null;
}

/** compactAge renders an elapsed duration as a single token — "40m", "6h",
 *  "12d". Measured against the moment the report was generated, not now: a
 *  report read next week still has to say how old the outage was when it was
 *  taken. */
export function compactAge(
  from: string | null | undefined,
  asOf?: string | null,
): string | null {
  const start = parseApiDate(from);
  if (!start) return null;
  const end = parseApiDate(asOf) || new Date();
  const ms = end.getTime() - start.getTime();
  if (ms < 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${Math.max(minutes, 1)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** ageHours is compactAge's raw form, for deciding what counts as chronic. */
export function ageHours(from: string | null | undefined, asOf?: string | null): number | null {
  const start = parseApiDate(from);
  if (!start) return null;
  const end = parseApiDate(asOf) || new Date();
  const ms = end.getTime() - start.getTime();
  return ms < 0 ? null : ms / 3600000;
}

/** StateTag is the one way a target's state is written down in a report: the
 *  state, how long it has been in it, and the chain that got it there on hover.
 *
 *  It replaced a bare tag plus a clock icon. The icon was a hover affordance
 *  carrying no information of its own; the age is the information, and it is
 *  also the thing worth hovering. */
export const StateTag: React.FC<{ item: ReportCheckItem; asOf?: string | null }> = ({ item, asOf }) => {
  const timeline = item.statusTimeline || [];
  const since = problemSince(item);
  const age = compactAge(since, asOf);
  const hours = ageHours(since, asOf);
  const chronic = hours !== null && hours >= CHRONIC_HOURS;

  const body = (
    <span style={{ whiteSpace: 'nowrap' }}>
      <Tag color={STATE_TAG_COLORS[item.state] || 'default'} style={{ margin: 0 }}>{item.state}</Tag>
      {age && (
        // Weight, not colour: colour on this screen means status, and a second
        // colour next to the status tag would compete with it. Chronic failures
        // get the emphasis because they are the ones a fresh eye misreads as new.
        <Text
          type="secondary"
          style={{ fontSize: 12, marginLeft: 4, fontWeight: chronic ? 600 : 400 }}
        >
          {age}
        </Text>
      )}
    </span>
  );

  if (!since && timeline.length === 0) return body;

  return (
    <Tooltip
      title={
        <div style={{ fontSize: 12 }}>
          {since && (
            <div style={{ marginBottom: timeline.length ? 4 : 0 }}>
              Failing since {formatDateTime(since)}
              {age && ` — ${age}${chronic ? ' (chronic)' : ''} at report time`}
            </div>
          )}
          {timeline.map((c, i) => (
            <div key={`${c.state}-${c.at}-${i}`}>{c.state} · {formatDateTimeCompact(c.at)}</div>
          ))}
          {/* The chain holds escalations only, so its last entry is the worst
              the target reached — not necessarily the state this report caught
              it in. Without this line the tooltip contradicts the tag above it. */}
          {timeline.length > 0 && timeline[timeline.length - 1].state !== item.state && (
            <div>then {item.state} at report time</div>
          )}
        </div>
      }
    >
      <span style={{ cursor: 'help' }}>{body}</span>
    </Tooltip>
  );
};

const BAR_ORDER = ['healthy', 'suspect', 'degraded', 'zombie', 'error', 'down', 'stale', 'unknown'] as const;

// StatusBar is a stacked bar showing the status distribution of a group.
//
// It fills whatever it is placed in rather than taking a pixel width. The bar
// is a proportion, and a proportion drawn 300px wide inside a card that is 900
// reads as a card that failed to load — which is exactly how a fixed width
// looked next to the summary tags under it.
export const StatusBar: React.FC<{ breakdown: StatusBreakdown }> = ({ breakdown }) => {
  const { token } = theme.useToken();
  if (!breakdown || breakdown.total === 0) return <Text type="secondary">--</Text>;
  // Order matters: stale lives at the right end so it visually trails the
  // active failure states (down/error) — helps the eye separate "still
  // happening" from "settled long ago".
  const segments = BAR_ORDER
    .map((s) => ({ key: s, count: (breakdown as any)[s] || 0 }))
    .filter((s) => s.count > 0);

  return (
    <Tooltip
      title={
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Total: {breakdown.total}</div>
          {segments.map((s) => (
            <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ color: STATE_COLORS[s.key] }}>{STATE_LABELS[s.key]}</span>
              <span>{s.count} ({Math.round((s.count / breakdown.total) * 100)}%)</span>
            </div>
          ))}
        </div>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'help', width: '100%' }}>
        <span style={{ fontSize: 12, color: token.colorText, minWidth: 26, textAlign: 'right' }}>{breakdown.total}</span>
        <div style={{
          display: 'flex', flex: 1, minWidth: 60, height: 14,
          borderRadius: 3, overflow: 'hidden', background: token.colorFillSecondary,
        }}>
          {segments.map((s) => (
            <div
              key={s.key}
              style={{
                width: `${(s.count / breakdown.total) * 100}%`,
                backgroundColor: STATE_COLORS[s.key],
                minWidth: s.count > 0 ? 2 : 0,
              }}
            />
          ))}
        </div>
      </div>
    </Tooltip>
  );
};

// StatusTags is the compact tag list of nonzero counts.
export const StatusTags: React.FC<{ breakdown: StatusBreakdown }> = ({ breakdown }) => {
  if (!breakdown || breakdown.total === 0) return <Text type="secondary">--</Text>;
  return (
    <Space size={[4, 2]} wrap>
      {BAR_ORDER
        .filter((s) => ((breakdown as any)[s] || 0) > 0)
        .map((s) => (
          <Tag key={s} color={TAG_COLORS[s]} style={{ fontSize: 12, margin: 0 }}>
            {(breakdown as any)[s]} {STATE_LABELS[s]}
          </Tag>
        ))}
    </Space>
  );
};
